import { useId, useMemo, useRef, useState, type KeyboardEvent } from 'react';
import { X } from 'lucide-react';
import { cn } from '@/lib/cn';

interface SymptomPickerProps {
  options: readonly string[];
  value: string[];
  onChange: (value: string[]) => void;
  /** Offer "Add “…”" for a symptom not in the list. */
  allowCustom?: boolean;
  placeholder?: string;
  invalid?: boolean;
  disabled?: boolean;
  id?: string;
}

/**
 * Searchable multi-select: the chosen symptoms as removable chips, a search
 * box, and a list filtered as you type. Enter picks the highlighted entry
 * (or adds the typed text when allowCustom); Backspace in an empty box
 * removes the last chip; Escape closes the list. A WAI-ARIA combobox +
 * listbox, so it is usable without a mouse.
 */
export function SymptomPicker({ options, value, onChange, allowCustom, placeholder, invalid, disabled, id }: SymptomPickerProps) {
  const listId = useId();
  const inputRef = useRef<HTMLInputElement>(null);
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);

  const q = query.trim();
  const chosen = useMemo(() => new Set(value.map((v) => v.toLowerCase())), [value]);
  const matches = useMemo(
    () => options.filter((o) => !chosen.has(o.toLowerCase()) && o.toLowerCase().includes(q.toLowerCase())),
    [options, chosen, q]
  );
  const exact = !!q && (options.some((o) => o.toLowerCase() === q.toLowerCase()) || chosen.has(q.toLowerCase()));
  const canAddCustom = !!allowCustom && !!q && !exact;
  const entries = [...matches.map((m) => ({ label: m, custom: false })), ...(canAddCustom ? [{ label: q, custom: true }] : [])];

  const add = (label: string) => {
    if (!chosen.has(label.toLowerCase())) onChange([...value, label]);
    setQuery('');
    setActive(0);
    inputRef.current?.focus();
  };
  const remove = (label: string) => onChange(value.filter((v) => v !== label));

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setOpen(true); setActive((a) => Math.min(a + 1, entries.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActive((a) => Math.max(a - 1, 0)); }
    else if (e.key === 'Enter') {
      const pick = entries[active];
      if (open && pick) { e.preventDefault(); add(pick.label); }
    } else if (e.key === 'Escape') { setOpen(false); }
    else if (e.key === 'Backspace' && !query && value.length) { remove(value[value.length - 1]!); }
  };

  const showList = open && !disabled && entries.length > 0;

  return (
    <div className="relative">
      <div
        className={cn(
          'flex min-h-[38px] flex-wrap items-center gap-1.5 rounded-[var(--radius)] border border-line bg-card px-2 py-1.5',
          'focus-within:border-teal focus-within:ring-[3px] focus-within:ring-teal/25',
          invalid && 'border-danger focus-within:border-danger focus-within:ring-danger/15',
          disabled && 'opacity-70'
        )}
        onClick={() => inputRef.current?.focus()}
      >
        {value.map((v) => (
          <span key={v} className="inline-flex items-center gap-1 rounded-full bg-accent px-2.5 py-0.5 text-[12.5px] font-medium text-teal">
            {v}
            {!disabled && (
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); remove(v); }}
                aria-label={`Remove ${v}`}
                className="-mr-1 flex h-4 w-4 items-center justify-center rounded-full bg-transparent p-0 text-teal hover:bg-teal/15"
              >
                <X className="h-3 w-3" />
              </button>
            )}
          </span>
        ))}
        <input
          ref={inputRef}
          id={id}
          role="combobox"
          aria-expanded={showList}
          aria-controls={listId}
          aria-autocomplete="list"
          aria-activedescendant={showList ? `${listId}-${active}` : undefined}
          value={query}
          disabled={disabled}
          onChange={(e) => { setQuery(e.target.value); setOpen(true); setActive(0); }}
          onFocus={() => setOpen(true)}
          onBlur={() => setTimeout(() => setOpen(false), 120)}
          onKeyDown={onKeyDown}
          placeholder={value.length ? '' : placeholder || 'Search symptoms'}
          // Overrides index.css's `input { border; padding; width:100% }` —
          // this box borrows the wrapper's border and focus ring.
          className="w-auto min-w-[140px] flex-1 border-0 bg-transparent p-0.5 text-[13px] shadow-none outline-none"
        />
      </div>
      {showList && (
        <ul
          id={listId}
          role="listbox"
          aria-multiselectable="true"
          className="absolute left-0 right-0 z-30 mt-1 max-h-[220px] list-none overflow-y-auto rounded-[var(--radius)] border border-line bg-card p-1 shadow-lg"
        >
          {entries.map((en, i) => (
            <li
              key={`${en.custom ? 'custom:' : ''}${en.label}`}
              id={`${listId}-${i}`}
              role="option"
              aria-selected={i === active}
              onMouseDown={(e) => { e.preventDefault(); add(en.label); }}
              onMouseEnter={() => setActive(i)}
              className={cn('cursor-pointer rounded px-2.5 py-1.5 text-[13px]', i === active ? 'bg-accent text-teal' : 'text-ink')}
            >
              {en.custom ? <>Add “<b>{en.label}</b>”</> : en.label}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
